import type { AssignedTagDto, TagDefinitionDto, TagSubject } from "@safiri/shared";
import { api, unwrap } from "../lib/axios";
import type { ApiEnvelope } from "../types/api";

export type TagDefinition = TagDefinitionDto;
export type AssignedTag = AssignedTagDto;

// The rider tag routes say "riders"; the SPA calls them users elsewhere.
const base = (subject: TagSubject, id: string) =>
  `/admin/${subject === "driver" ? "drivers" : "riders"}/${id}/tags`;

export const tagsApi = {
  async list(withCounts = false): Promise<TagDefinition[]> {
    const res = await api.get<ApiEnvelope<TagDefinition[]>>("/admin/tags", {
      params: withCounts ? { counts: "true" } : undefined,
    });
    return unwrap(res);
  },
  async create(body: {
    key: string;
    label: string;
    description?: string | null;
    appliesTo: TagSubject[];
    color?: string;
  }): Promise<TagDefinition> {
    return unwrap(await api.post<ApiEnvelope<TagDefinition>>("/admin/tags", body));
  },
  async update(
    key: string,
    body: Partial<{ label: string; description: string | null; appliesTo: TagSubject[]; color: string; archived: boolean }>,
  ): Promise<TagDefinition> {
    return unwrap(await api.patch<ApiEnvelope<TagDefinition>>(`/admin/tags/${key}`, body));
  },
  async forAccount(subject: TagSubject, id: string): Promise<AssignedTag[]> {
    return unwrap(await api.get<ApiEnvelope<AssignedTag[]>>(base(subject, id)));
  },
  async assign(
    subject: TagSubject,
    id: string,
    body: { key: string; note?: string | null; expiresAt?: string | null },
  ): Promise<AssignedTag[]> {
    return unwrap(await api.post<ApiEnvelope<AssignedTag[]>>(base(subject, id), body));
  },
  async remove(subject: TagSubject, id: string, key: string, reason?: string): Promise<AssignedTag[]> {
    return unwrap(
      await api.delete<ApiEnvelope<AssignedTag[]>>(`${base(subject, id)}/${key}`, { data: { reason } }),
    );
  },
};

/** Is an assigned tag still in force? */
export const isActiveTag = (t: Pick<AssignedTag, "expiresAt">) =>
  !t.expiresAt || new Date(t.expiresAt).getTime() > Date.now();
